import mapboxgl from 'mapbox-gl';
import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api';
import { API_BASE } from '../apiBase';
import { reportEditorData } from '../reportSnapshot';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Close as CloseIcon, Person as UserIcon } from '@mui/icons-material';
import BackupTableOutlinedIcon from '@mui/icons-material/BackupTableOutlined';
import Calendar from '@mui/icons-material/CalendarToday';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Badge,
  Box,
  Button,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as turf from '@turf/turf';
import CompDashboard from './CompDashboard';
import CompPreview from './CompPreview';
import CompReportsSidebar from './CompReportsSidebar.js';
import PropertyCartSidebar from './PropertyCartSidebar';
import PropertyFilterSidebar from './PropertyFilterSidebar';
import ReportBuilder from './ReportBuilder';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY;

class TooltipControl {
  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'custom-tooltip-container';
    this.container.style.display = 'none';
    this.container.style.position = 'absolute';
    this.container.style.pointerEvents = 'none';
    this.container.style.zIndex = '1000';
    this.container.style.backgroundColor = 'white';
    this.container.style.padding = '8px 12px';
    this.container.style.borderRadius = '4px';
    this.container.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
    this.container.style.fontFamily = 'sans-serif';
    this.container.style.fontSize = '14px';
    this.container.style.maxWidth = '250px';
    this.container.style.transform = 'translate(-50%, -100%)';
    this.container.style.marginTop = '-10px';
  }

  onAdd(map) {
    this._map = map;
    // Append to map container instead of as a control
    this._map.getContainer().appendChild(this.container);
    return this.container;
  }

  show(content, lngLat) {
    this.container.innerHTML = content;
    this.container.style.display = 'block';
    const pos = this._map.project(lngLat);
    this.container.style.left = `${pos.x}px`;
    this.container.style.top = `${pos.y}px`;
  }

  hide() {
    this.container.style.display = 'none';
  }

  onRemove() {
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this._map = undefined;
  }
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

const MapPage = ({
  user,
  userData,
  onEditProfile,
  refreshUserData,
  organizations = [],
  onOrganizationChange,
}) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const activePopupRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedPolygons, setSelectedPolygons] = useState(new Set());
  const hoverPopupRef = useRef(
    new mapboxgl.Popup({ closeButton: false, closeOnClick: false }),
  );
  const shapeToPolygonsMap = useRef({});
  const [cartItems, setCartItems] = useState([]); // Array of site metadata
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedUseTypes, setSelectedUseTypes] = useState([]);
  const [minAcreage, setMinAcreage] = useState('');
  const [maxAcreage, setMaxAcreage] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const dmreMarkersRef = useRef([]);
  const [isSearching, setIsSearching] = useState(false);
  const tooltipControlRef = useRef(null);
  const isMobile = () => /Mobi|Android/i.test(navigator.userAgent);
  const lastTappedPolygonIdRef = useRef(null);
  const lastTapTimestampRef = useRef(0);
  const [pdfUrl, setPdfUrl] = useState(null); // 👈 Blob URL to show in iframe
  const [pdfBlob, setPdfBlob] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [compReports, setCompReports] = useState([]);
  const [showCompReports, setShowCompReports] = useState(false);
  const [isLoadingCompReports, setIsLoadingCompReports] = useState(false);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false); // For polygons/search
  const [isLoadingReports, setIsLoadingReports] = useState(false); // For comp reports
  const isLoadingAnything = isSearching || isLoadingCompReports;
  const [hasInitialSearchCompleted, setHasInitialSearchCompleted] =
    useState(false);
  const [showSecondaryIcons, setShowSecondaryIcons] = useState(false);
  const [shouldShowGenerateButton, setShouldShowGenerateButton] =
    useState(true);
  const [reportGeoData, setReportGeoData] = useState({});
  const [orderedGeoData, setOrderedGeoData] = useState(null);
  const [reportBuilderState, setReportBuilderState] = useState({
    // ... your existing state
    mapState: null, // Add this
  });
  const [liveRows, setLiveRows] = useState(null); // 👈 store live, editable rows
  const [dashboardReports, setDashboardReports] = useState({
    userReports: [],
    officeReports: [],
  });
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [reportEditingContext, setReportEditingContext] = useState({
    id: null, // report_id
    status: 'draft', // current status
  });
  const [currentPolygonFeatures, setCurrentPolygonFeatures] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchingByName, setIsSearchingByName] = useState(false);
  const [showNameSearch, setShowNameSearch] = useState(false);
  const officeLocations = {
    DFW: { lng: -96.82363023170578, lat: 32.95279712939538 },
    Houston: { lng: -95.3698, lat: 29.7604 },
    'Central Texas': { lng: -97.7431, lat: 30.2672 },
    Phoenix: { lng: -112.074, lat: 33.4484 },
  };

  const [navigationState, setNavigationState] = useState({
    showMap: true,
    showDashboard: false,
    showReport: false,
    showPreview: false,
    editingReport: null,
  });

  const [mapViewState, setMapViewState] = useState(() => {
    // Default to DFW if no office or invalid office
    const defaultLocation = officeLocations.DFW;

    // Get user's office location if available
    const userOffice = userData?.office && officeLocations[userData.office];

    return {
      center: userOffice
        ? [userOffice.lng, userOffice.lat]
        : [defaultLocation.lng, defaultLocation.lat],
      zoom: 12,
    };
  });

  // Navigation handlers
  const handleShowDashboard = () => {
    // Capture current map state before navigating away
    setReportBuilderState((prev) => ({
      ...prev,
      mapState: {
        center: mapRef.current.getCenter(),
        zoom: mapRef.current.getZoom(),
        bounds: mapRef.current.getBounds(),
      },
    }));

    setNavigationState({
      showMap: false,
      showDashboard: true,
      showReport: false,
      showPreview: false,
      editingReport: null,
    });
  };

  const handleNavigateToMap = () => {
    setNavigationState({
      showMap: true,
      showDashboard: false,
      showReport: false,
      showPreview: false,
      editingReport: null,
    });
  };

  const handleEditReport = (report) => {
    const snapshot = reportEditorData(report);
    setReportBuilderState(null);
    setReportData(snapshot);
    setLiveRows([...snapshot.offers, ...snapshot.feedback]);

    // 2️⃣ Store editing context
    setReportEditingContext({
      id: report.report_id,
      status: report.status,
    });

    // 3️⃣ Navigate to ReportBuilder view
    setNavigationState({
      showMap: false,
      showDashboard: false,
      showReport: true,
      showPreview: false,
      editingReport: report,
    });
  };

  const handleReturnToMap = (mapData = null) => {
    // First preserve all ReportBuilder state
    console.log('Restoring builder state:', {
      offers: mapData?.offers?.length,
      feedback: mapData?.feedback?.length,
    });

    if (mapData) {
      console.log('DEBUG - Restoring builder state with:', {
        offers: mapData?.offers?.length,
        feedback: mapData?.feedback?.length,
      });

      setReportBuilderState((prev) => ({
        ...prev,
        rows: mapData.currentRows || [],
        cartItems: mapData.currentCartItems || [],
        rowOrder: mapData.currentRowOrder || [],
        columnSettings: mapData.columnSettings || {},
        geoData: mapData.reportGeoData || {},
        dataGridState: mapData.dataGridState || null,
        excludeOffers: mapData.excludeOffers || false,
        excludeFeedback: mapData.excludeFeedback || false,
        offers: Array.isArray(mapData.offers) ? [...mapData.offers] : [],
        feedback: Array.isArray(mapData.feedback) ? [...mapData.feedback] : [],
        offerRowOrder: Array.isArray(mapData.offerRowOrder)
          ? [...mapData.offerRowOrder]
          : [],
        feedbackRowOrder: Array.isArray(mapData.feedbackRowOrder)
          ? [...mapData.feedbackRowOrder]
          : [],
        activeTab: mapData.activeTab || 0,
      }));

      if (mapData.reportGeoData) {
        setReportGeoData(mapData.reportGeoData);
      }
    }

    // Navigate back to map view
    setNavigationState({
      showMap: true,
      showDashboard: false,
      showReport: false,
      showPreview: false,
      editingReport: null,
    });

    // Fly to the correct location
    if (mapRef.current) {
      const targetCenter = mapData?.mapState?.center || mapViewState.center;
      const targetZoom = mapData?.mapState?.zoom || mapViewState.zoom;

      if (targetCenter && targetCenter.lng && targetCenter.lat) {
        mapRef.current.flyTo({
          center: [targetCenter.lng, targetCenter.lat],
          zoom: targetZoom,
          essential: true,
        });
      } else {
        // Fallback to user's office location or DFW
        const officeLocation =
          officeLocations[userData?.office] || officeLocations.DFW;
        mapRef.current.flyTo({
          center: [officeLocation.lng, officeLocation.lat],
          zoom: 12,
          essential: true,
        });
      }
    }

    // Only fetch with saved bounds if we have bounds data
    if (mapData?.mapState?.bounds) {
      const fetchWithSavedBounds = async () => {
        setIsLoadingProperties(true);
        try {
          const bounds = mapData.mapState.bounds;
          const params = {
            minLat: bounds.getSouth(),
            maxLat: bounds.getNorth(),
            minLng: bounds.getWest(),
            maxLng: bounds.getEast(),
          };

          const response = await apiFetch(`${API_BASE}/listings/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const geojson = await response.json();

          if (!geojson?.features) {
            throw new Error('Invalid GeoJSON data received');
          }

          // Preserve selections during render
          const selectionsToRestore = new Set([
            ...selectedPolygons,
            ...(mapData.selectedIds || []),
          ]);

          renderProperties(mapRef.current, geojson, selectionsToRestore);

          // Restore selected features
          const selectedFeatures = geojson.features
            .filter((f) => mapData.selectedIds?.includes(f.id))
            .map((f) => ({
              id: f.id,
              geometry: f.geometry,
              properties: {
                ...f.properties,
                listingId: f.properties.listingId || f.id,
                listingName: f.properties.listingName || 'Unnamed Property',
              },
            }));

          const combinedCartItems = [
            ...cartItems.filter(
              (item) => !mapData.selectedIds?.includes(item.id),
            ),
            ...selectedFeatures,
          ];

          setCartItems(combinedCartItems);
          setSelectedPolygons(
            new Set(combinedCartItems.map((item) => item.id)),
          );

          // Update feature states
          combinedCartItems.forEach((item) => {
            const id = item.id;
            if (
              mapRef.current
                .querySourceFeatures('property-polygons')
                .some((f) => f.id === id)
            ) {
              mapRef.current.setFeatureState(
                { source: 'property-polygons', id },
                { selected: true },
              );
            }
          });
        } catch (err) {
          console.error('Error loading saved report:', err);
          // Fallback to current cart items
          setCartItems((prev) => [...prev]);
          setSelectedPolygons((prev) => new Set(prev));
        } finally {
          setIsLoadingProperties(false);
        }
      };

      fetchWithSavedBounds();
    }
  };

  const handleShowPreview = (pdfUrl, reportData, generatedPdfBlob) => {
    setReportEditingContext((prev) => ({
      ...prev,
      id: reportData.report_id || prev.id,
      status: reportData.status || prev.status,
    }));

    setReportBuilderState({
      // Existing state
      rows: reportData.currentRows,
      cartItems: reportData.currentCartItems,
      rowOrder: reportData.currentRowOrder,
      columnSettings: reportData.columnSettings,
      reportTitle: reportData.reportTitle,
      geoData: reportData.geoData,
      reportId: reportData.report_id,
      reportStatus: reportData.status,

      // New state for sorting/filtering
      offers: reportData.offers || [],
      feedback: reportData.feedback || [],
      offerRowOrder: reportData.offerRowOrder || [],
      feedbackRowOrder: reportData.feedbackRowOrder || [],
      excludeOffers: reportData.excludeOffers || false,
      excludeFeedback: reportData.excludeFeedback || false,

      // DataGrid state
      offerSortModel: reportData.dataGridState?.offerSortModel || [],
      feedbackSortModel: reportData.dataGridState?.feedbackSortModel || [],
      statusFilter: reportData.statusFilter || [],
      dateFilter: reportData.dateFilter || null,
      activeTab: reportData.activeTab || 0,
    });

    setNavigationState((prev) => ({
      ...prev,
      showPreview: true,
      showReport: false,
    }));

    setPdfUrl(pdfUrl);
    setPdfBlob(generatedPdfBlob ?? null);
    setReportData(reportData);
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const updateViewState = () => {
      setMapViewState({
        center: map.getCenter().toArray(),
        zoom: map.getZoom(),
      });
    };

    map.on('moveend', updateViewState);

    return () => {
      map.off('moveend', updateViewState);
    };
  }, []);

  const touchStateRef = useRef({
    lastTappedId: null,
    lastTapTime: 0,
    pendingTap: null,
    tapCount: 0, // Track consecutive taps
  });

  const handleClearAll = () => {
    const map = mapRef.current;
    if (!map) return;

    // Clear all selected polygons on the map
    cartItems.forEach((item) => {
      if (map.getSource('property-polygons')) {
        map.setFeatureState(
          { source: 'property-polygons', id: item.id },
          { selected: false },
        );
      }
    });

    // Clear the state
    setSelectedPolygons(new Set());
    setCartItems([]);
  };

  useEffect(() => {
    if (!navigationState.showPreview && !navigationState.showReport) {
      // When returning to map view
      const timer = setTimeout(() => {
        if (mapRef.current) {
          // Make container visible again
          mapContainer.current.style.display = 'block';

          // Force map repaint
          mapRef.current.resize();
          mapRef.current.triggerRepaint();
        }
      }, 50);

      return () => clearTimeout(timer);
    } else {
      // When going to report/preview, hide but don't destroy
      if (mapContainer.current) {
        mapContainer.current.style.display = 'none';
      }
    }
  }, [navigationState.showPreview, navigationState.showReport, mapViewState]);

  const addPolygonToSelection = (featureId) => {
    if (!featureId) return;

    const map = mapRef.current;
    if (!map || selectedPolygons.has(featureId)) return; // Already selected

    const feature = map
      .querySourceFeatures('property-polygons')
      .find((f) => f.id === featureId);

    if (feature) {
      map.setFeatureState(
        { source: 'property-polygons', id: featureId },
        { selected: true },
      );

      setSelectedPolygons((prev) => {
        const updated = new Set(prev);
        updated.add(featureId);
        return updated;
      });

      setCartItems((prev) => {
        const alreadyInCart = prev.some((item) => item.id === featureId);
        return alreadyInCart ? prev : [...prev, feature];
      });
    }
  };

  const removeFromCart = (featureId) => {
    const map = mapRef.current;
    if (!map || !featureId) return;

    map.setFeatureState(
      { source: 'property-polygons', id: featureId },
      { selected: false },
    );
    setSelectedPolygons((prev) => {
      const updated = new Set(prev);
      updated.delete(featureId);
      return updated;
    });
    setCartItems((prev) => prev.filter((item) => item.id !== featureId));
  };

  const togglePolygonSelection = (featureId) => {
    if (!featureId) {
      console.warn('🚨 No featureId provided to togglePolygonSelection');
      return;
    }

    const map = mapRef.current;
    if (!map) return;

    // Check both the map's feature state and local state
    const isSelected =
      map.getFeatureState({ source: 'property-polygons', id: featureId })
        ?.selected || selectedPolygons.has(featureId);

    if (isSelected) {
      // Deselect the polygon
      map.setFeatureState(
        { source: 'property-polygons', id: featureId },
        { selected: false },
      );
      setSelectedPolygons((prev) => {
        const updated = new Set(prev);
        updated.delete(featureId);
        return updated;
      });
      setCartItems((prev) => prev.filter((item) => item.id !== featureId));

      // Remove from reportGeoData
      setReportGeoData((prev) => {
        const newData = { ...prev };
        delete newData[featureId];
        console.log(
          '🔴 Removed from reportGeoData:',
          featureId,
          'Current:',
          newData,
        );
        return newData;
      });
    } else {
      // Select the polygon
      const feature = map
        .querySourceFeatures('property-polygons')
        .find((f) => f.id === featureId);

      if (feature) {
        // VISUALLY deselect all polygons before selecting this one
        map.querySourceFeatures('property-polygons').forEach((f) => {
          const fid = f.id;
          if (fid !== featureId) {
            map.setFeatureState(
              { source: 'property-polygons', id: fid },
              { selected: false },
            );
          }
        });

        // VISUALLY select this one
        map.setFeatureState(
          { source: 'property-polygons', id: featureId },
          { selected: true },
        );

        // STATE updates: single selection only
        setSelectedPolygons(new Set([featureId]));
        setCartItems([feature]);
        setReportGeoData({
          [featureId]: {
            geometry: feature.geometry,
            properties: {
              listingName:
                feature.properties.listingName || `Listing ${featureId}`,
              acreage: feature.properties.acreage || 0,
              latitude: feature.properties.latitude || 0,
              longitude: feature.properties.longitude || 0,
            },
          },
        });
      }
    }
  };

  const addToCart = (id) => {
    if (!id) {
      console.warn('addToCart called with undefined id');
      return;
    }
    addPolygonToSelection(id);
  };

  const setLayerInteractivity = (map, interactive) => {
    const layerId = 'property-polygons-layer';

    if (!map.getLayer(layerId)) return;

    // Always remove the event listener first to avoid duplicates
    map.off('click', layerId, polygonClickHandler);

    if (interactive) {
      // Add the event listener only if interactive is true
      map.on('click', layerId, polygonClickHandler);
    }
  };

  function polygonClickHandler(e) {
    const feature = e.features[0];
    const id = feature.id || feature.properties?.id;

    if (feature.properties.fillType === 'blue') {
      togglePolygonSelection(id);
    }
  }

  const getTooltipContent = (feature) => {
    const props = feature.properties;
    const isBlue = props.fillType === 'blue';
    console.log(props);
    let html = `<div class="custom-tooltip ${isBlue ? 'blue-tooltip' : 'grey-tooltip'}">`;

    // Common fields
    html += `<strong>${props.listingName || props.name}</strong><br/>`;

    if (isBlue) {
      // Blue polygon tooltip (comps)
      html += `
        ${props.primaryUseType ? `Use: ${props.primaryUseType}<br/>` : ''}
        ${props.acreage ? `Acreage: ${props.acreage} acres<br/>` : ''}
        ${props.leadBroker ? `Lead Broker: ${props.leadBroker}<br/>` : ''}
        ${props.commencementDate ? `Commencement Date: ${new Date(props.commencementDate).toLocaleDateString()}<br/>` : ''}
        ${props.sellerRepStatus ? `Listing Status: ${props.sellerRepStatus}<br/>` : ''}
      `;
    }
    html += `</div>`;
    return html;
  };

  const handleTouchEnd = (e) => {
    if (!isMobile()) return;
    e.preventDefault();

    const point = [e.point.x, e.point.y];
    const bbox = [
      [point[0] - 5, point[1] - 5],
      [point[0] + 5, point[1] + 5],
    ];
    const features = mapRef.current.queryRenderedFeatures(bbox, {
      layers: ['property-polygons-layer'],
    });

    if (!features.length) {
      tooltipControlRef.current?.hide();
      touchStateRef.current = {
        lastTappedId: null,
        lastTapTime: 0,
        pendingTap: null,
        tapCount: 0,
      };
      return;
    }

    const feature = features[0];
    const id = feature.id || feature.properties?.id;
    const now = Date.now();
    const state = touchStateRef.current;

    // If same polygon and within 800ms, increment tap count
    if (state.lastTappedId === id && now - state.lastTapTime < 800) {
      touchStateRef.current.tapCount++;
    } else {
      // New tap sequence
      touchStateRef.current.tapCount = 1;
    }

    // Update last tap info
    touchStateRef.current.lastTappedId = id;
    touchStateRef.current.lastTapTime = now;

    // Clear any pending single tap action
    if (state.pendingTap) {
      clearTimeout(state.pendingTap);
    }

    // Show tooltip immediately on first tap
    const coords = e.lngLat;
    const props = feature.properties;
    const tooltipHTML = getTooltipContent(feature);
    tooltipControlRef.current?.show(tooltipHTML, coords);

    // Set timeout for double-tap detection
    touchStateRef.current.pendingTap = setTimeout(() => {
      // If we get here without a second tap, it's a single tap
      if (touchStateRef.current.tapCount === 1) {
        tooltipControlRef.current?.hide();
      }
      // Reset tap count after timeout
      touchStateRef.current.tapCount = 0;
    }, 800); // Increased to 800ms for better UX

    // Check for double-tap (count of 2 or more)
    if (touchStateRef.current.tapCount >= 2) {
      //console.log("✅ DOUBLE TAP DETECTED");
      clearTimeout(touchStateRef.current.pendingTap);
      tooltipControlRef.current?.hide();

      if (feature.properties.fillType === 'blue') {
        togglePolygonSelection(id);
      } else {
        window.open(`https://factor.dmre.com/property-detail/${id}`, '_blank');
      }

      // Reset state
      touchStateRef.current = {
        lastTappedId: null,
        lastTapTime: 0,
        pendingTap: null,
        tapCount: 0,
      };
    }
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const handleTouchStart = (e) => {
      if (!e.originalEvent.target.closest('.mapboxgl-canvas')) {
        // Only reset if we're not touching a feature
        touchStateRef.current = {
          lastTappedId: null,
          lastTapTime: 0,
          pendingTap: null,
        };
        tooltipControlRef.current?.hide();
      }
    };

    map.on('touchstart', handleTouchStart);
    map.on('touchend', handleTouchEnd);

    return () => {
      map.off('touchstart', handleTouchStart);
      map.off('touchend', handleTouchEnd);
      // Clean up any pending timeouts
      if (touchStateRef.current.pendingTap) {
        clearTimeout(touchStateRef.current.pendingTap);
      }
    };
  }, [mapRef.current]);

  useEffect(() => {
    if (!mapContainer.current || !userData?.office) return;

    const officeLocation = officeLocations[userData.office];
    if (!officeLocation) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: mapViewState.center,
      zoom: mapViewState.zoom,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Force z-index after controls are added
    setTimeout(() => {
      // Navigation controls
      const navControls = document.querySelectorAll(
        '.mapboxgl-ctrl-top-right, .mapboxgl-ctrl-group',
      );
      navControls.forEach((ctrl) => {
        ctrl.style.zIndex = '111';
      });
    }, 100);

    map.on('load', () => {
      mapRef.current = map;
      tooltipControlRef.current = new TooltipControl();
      map.getContainer().appendChild(tooltipControlRef.current.onAdd(map));

      map.on('moveend', () => {
        const center = map.getCenter();
        setMapViewState({
          center: { lng: center.lng, lat: center.lat },
          zoom: map.getZoom(),
        });
      });

      // Handle clicks on the listing layer
      map.on('click', 'property-polygons-layer', polygonClickHandler);

      // Handle clicks on the listing layer
      map.touchZoomRotate.enable(); // Ensures touch gestures work

      map.on('touchstart', (e) => {
        // If the user touches the map without hitting a feature
        if (!e.originalEvent.target.closest('.mapboxgl-canvas')) return;

        tooltipControlRef.current?.hide();
        lastTappedPolygonIdRef.current = null;
        lastTapTimestampRef.current = 0;
      });
    });

    return () => {
      if (map) {
        // 1. Remove tooltip control if it exists
        if (tooltipControlRef.current) {
          map.removeControl(tooltipControlRef.current);
        }

        // 2. Remove existing event listeners
        map.off('click', 'property-polygons-layer', polygonClickHandler);
        map.off('mousemove', 'property-polygons-layer');
        map.off('mouseenter', 'property-polygons-layer');
        map.off('mouseleave', 'property-polygons-layer');

        // 3. Clean up any active popups
        if (hoverPopupRef.current) {
          hoverPopupRef.current.remove();
        }
        if (activePopupRef.current) {
          activePopupRef.current.remove();
        }

        // 4. Remove the map instance
        map.remove();
      }
    };
  }, [userData]);

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedUseTypes.length > 0) count++;
    if (minAcreage) count++;
    if (maxAcreage) count++;
    return count;
  };

  const handleFetchRecords = async () => {
    setIsLoadingProperties(true);
    setShowSecondaryIcons(false);
    setSearchResults([]); // Clear previous search results
    setSearchTerm(''); // Clear search term

    // Only hide search field if it's the initial search
    if (!hasInitialSearchCompleted) {
      setShowNameSearch(false);
    }

    // Clear search results but keep the term
    setSearchResults([]);

    try {
      const map = mapRef.current;
      const bounds = map.getBounds();

      const params = {
        minLat: bounds.getSouth(),
        maxLat: bounds.getNorth(),
        minLng: bounds.getWest(),
        maxLng: bounds.getEast(),
      };

      // 1. Fetch properties (essential)
      const propertiesResponse = await apiFetch(`${API_BASE}/listings/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...params,
          useTypes: selectedUseTypes,
          minAcreage: minAcreage ? parseFloat(minAcreage) : undefined,
          maxAcreage: maxAcreage ? parseFloat(maxAcreage) : undefined,
        }),
      });

      // Process property data
      const geojson = await propertiesResponse.json();
      //console.log("Geojson: ", geojson);
      const savedSelections = new Set(selectedPolygons);

      // Process and render properties immediately
      renderProperties(map, geojson, savedSelections);

      // 2. Fetch comp reports (lightweight)
      await fetchCompReports(params);

      // After first successful search, show the secondary icons
      if (!hasInitialSearchCompleted) {
        setHasInitialSearchCompleted(true);
        setShowNameSearch(true);
      }
      setShowSecondaryIcons(true);
    } catch (err) {
      console.error('Failed to fetch records:', err);
    } finally {
      setIsLoadingProperties(false);
    }
  };

  const renderProperties = (map, geojson, savedSelections) => {
    // Remove existing DMRE markers
    setCurrentPolygonFeatures(geojson.features);
    dmreMarkersRef.current.forEach((marker) => marker.remove());
    dmreMarkersRef.current = [];

    // Process features and add DMRE markers
    geojson.features.forEach((feature) => {
      feature.properties.interactive = true;
      feature.id = feature.properties.id;

      if (feature.properties.isDMRE) {
        const { latitude, longitude, name } = feature.properties;
        if (latitude && longitude) {
          const el = document.createElement('div');
          el.className = 'dmre-marker';
          el.style.backgroundImage =
            "url('https://factor-mind-assets.s3.us-east-1.amazonaws.com/factor-logo-blue.png')";
          el.style.width = '28px';
          el.style.height = '28px';
          el.style.backgroundSize = 'contain';
          el.style.backgroundRepeat = 'no-repeat';
          el.style.backgroundPosition = 'center';
          el.style.zIndex = '10';

          const marker = new mapboxgl.Marker(el)
            .setLngLat([longitude, latitude])
            .addTo(map);

          setTimeout(() => {
            if (marker._element) marker._element.style.zIndex = '10';
          }, 50);

          dmreMarkersRef.current.push(marker);
        }
      }
    });

    // Update map layers
    if (map.getLayer('property-polygons-layer'))
      map.removeLayer('property-polygons-layer');
    if (map.getLayer('property-borders')) map.removeLayer('property-borders');
    if (map.getSource('property-polygons'))
      map.removeSource('property-polygons');

    map.addSource('property-polygons', {
      type: 'geojson',
      data: geojson,
      promoteId: 'id',
      generateId: false,
    });

    map.addLayer({
      id: 'property-polygons-layer',
      type: 'fill',
      source: 'property-polygons',
      paint: {
        'fill-color': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          '#003366',
          ['==', ['get', 'fillType'], 'blue'],
          '#00A8E8',
          ['==', ['get', 'fillType'], 'grey'],
          '#FFFFFF',
          '#000000',
        ],
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          0.85,
          ['==', ['get', 'fillType'], 'blue'],
          0.9,
          ['==', ['get', 'fillType'], 'grey'],
          0.3,
          0.3,
        ],
      },
    });

    map.addLayer({
      id: 'property-borders',
      type: 'line',
      source: 'property-polygons',
      paint: {
        'line-color': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          '#00C4FF',
          ['==', ['get', 'fillType'], 'blue'],
          '#0077B6',
          ['==', ['get', 'fillType'], 'grey'],
          '#03FAFA',
          '#333',
        ],
        'line-width': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          3,
          ['==', ['get', 'fillType'], 'blue'],
          3,
          ['==', ['get', 'fillType'], 'grey'],
          2,
          1,
        ],
      },
    });

    // Re-apply selection state from previous view
    savedSelections.forEach((id) => {
      map.setFeatureState(
        { source: 'property-polygons', id },
        { selected: true },
      );
    });
    setSelectedPolygons(savedSelections); // Sync local state

    // Update event handlers
    map.off('mousemove', 'property-polygons-layer');
    map.off('mouseenter', 'property-polygons-layer');
    map.off('mouseleave', 'property-polygons-layer');

    // Show tooltip on hover
    map.on('mousemove', 'property-polygons-layer', (e) => {
      const feature = e.features[0];
      tooltipControlRef.current?.show(getTooltipContent(feature), e.lngLat);
    });

    map.on('mouseleave', 'property-polygons-layer', () => {
      if (tooltipControlRef.current) {
        tooltipControlRef.current.hide();
      }
      map.getCanvas().style.cursor = '';
    });

    // Restore selections
    setSelectedPolygons((prev) => {
      const newSelections = new Set(prev);
      geojson.features.forEach((feature) => {
        const id = feature.id;
        if (newSelections.has(id)) {
          map.setFeatureState(
            { source: 'property-polygons', id },
            { selected: true },
          );
        }
      });
      return newSelections;
    });

    console.log(
      'Listing polygons:',
      geojson.features.filter((f) => f.properties.fillType === 'blue').length,
    );
  };

  // Separate function for comp reports only
  const fetchCompReports = async (params) => {
    try {
      setIsLoadingCompReports(true);
      setCompReports([]); // Clear existing reports first
      const compReportsResponse = await apiFetch(
        `${API_BASE}/listings/reports?${new URLSearchParams(params)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } },
      );

      const reportsData = await compReportsResponse.json();
      if (reportsData.success) {
        setCompReports(reportsData.reports);
        //console.log("COMP REPORTS DATA: ", reportsData.reports);
      }
    } catch (err) {
      console.error('Failed to fetch comp reports:', err);
    } finally {
      setIsLoadingCompReports(false);
    }
  };

  // Add this function to get minimal map data
  const getCartMapData = () => {
    if (!mapRef.current || cartItems.length === 0) {
      console.log('getCartMapData: No map reference or empty cart items');
      return null;
    }

    const mapData = {
      // Current map view (for context)
      center: mapRef.current.getCenter(),
      zoom: mapRef.current.getZoom(),

      // Only the geometries of cart items
      selectedGeometries: cartItems.map((item) => ({
        id: item.id,
        type: item.geometry.type,
        coordinates: item.geometry.coordinates,
        properties: {
          name: item.properties.listingName || `Property ${item.id}`,
          // Include any other minimal properties needed
        },
      })),
    };

    console.log('getCartMapData output:', {
      center: {
        lng: mapData.center.lng,
        lat: mapData.center.lat,
      },
      zoom: mapData.zoom,
      selectedGeometries: mapData.selectedGeometries.map((geo) => ({
        id: geo.id,
        type: geo.type,
        coordinates: geo.coordinates ? 'EXISTS' : 'MISSING', // Shows if coordinates exist
        properties: geo.properties,
      })),
      rawData: mapData, // The complete raw data
    });

    return mapData;
  };

  const handleSearchByName = () => {
    if (!searchTerm.trim()) return;

    setIsSearchingByName(true);

    try {
      const results = currentPolygonFeatures
        .filter((feature) => {
          // Skip features with invalid geometries
          if (!feature.geometry || !feature.geometry.coordinates) return false;

          const name =
            feature.properties?.listingName || feature.properties?.name || '';
          return name.toLowerCase().includes(searchTerm.toLowerCase());
        })
        // Remove duplicates by ID
        .filter(
          (feature, index, self) =>
            index === self.findIndex((f) => f.id === feature.id),
        );

      setSearchResults(results);

      if (results.length > 0) {
        const firstResult = results[0];
        if (firstResult.geometry && firstResult.geometry.coordinates) {
          const coordinates =
            turf.centerOfMass(firstResult).geometry.coordinates;
          mapRef.current.flyTo({
            center: coordinates,
            zoom: 14,
            essential: true,
          });
        }
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearchingByName(false);
    }
  };

  useEffect(() => {
    return () => {
      setSearchTerm('');
      setSearchResults([]);
      setShowNameSearch(false);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          height: '70px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
          flexShrink: 0,
        }}
      >
        <img
          src='/factor_listings_logo.png'
          alt='Factor Listings'
          style={{ width: '200px', height: 'auto' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {organizations.length > 1 && (
            <Select
              size='small'
              value={userData.organizationId}
              inputProps={{ 'aria-label': 'Organization' }}
              onChange={(event) => onOrganizationChange(event.target.value)}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          )}
          <Tooltip title='Listing Reports Dashboard'>
            <IconButton onClick={handleShowDashboard} sx={{ color: 'grey' }}>
              <BackupTableOutlinedIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onEditProfile} sx={{ color: 'grey' }}>
            <UserIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
        }}
      >
        {/* Map Container */}
        <Box
          ref={mapContainer}
          sx={{
            flex: 1,
            display:
              navigationState.showReport || navigationState.showPreview
                ? 'none'
                : 'block',
            touchAction: navigationState.showDashboard
              ? 'none'
              : 'pan-x pan-y pinch-zoom',
            position: 'relative',
            width: '100%',
            height: '100%',
          }}
        />

        {/* Floating controls */}
        {!navigationState.showPreview && !navigationState.showReport && (
          <>
            {/* Top-left buttons */}
            <Box
              sx={{
                position: 'absolute',
                top: 30,
                left: 24,
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Tooltip title='Show Records'>
                <IconButton
                  onClick={handleFetchRecords}
                  disabled={isLoadingProperties}
                  sx={{
                    backgroundColor: 'white',
                    boxShadow: 3,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                      transform: isLoadingProperties ? 'none' : 'scale(1.1)',
                    },
                  }}
                >
                  {isLoadingProperties ? (
                    <CircularProgress size={24} />
                  ) : (
                    <SearchIcon />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title='Filter Sites'>
                <Badge
                  badgeContent={getActiveFilterCount()}
                  color='primary'
                  invisible={getActiveFilterCount() === 0}
                >
                  <IconButton
                    onClick={() => setFilterOpen(true)}
                    sx={{
                      backgroundColor: 'white',
                      boxShadow: 3,
                      '&:hover': { backgroundColor: '#f0f0f0' },
                    }}
                  >
                    <TuneIcon />
                  </IconButton>
                </Badge>
              </Tooltip>
            </Box>

            {/* Search by name field */}
            {showNameSearch && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 30,
                  left: 80,
                  zIndex: 1000,
                  width: '250px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  boxShadow: 3,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  opacity: isLoadingProperties ? 0.6 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              >
                <TextField
                  fullWidth
                  size='small'
                  placeholder={
                    isLoadingProperties ? 'Searching...' : 'Search by name...'
                  }
                  value={searchTerm}
                  onChange={(e) =>
                    !isLoadingProperties && setSearchTerm(e.target.value)
                  }
                  onKeyPress={(e) =>
                    e.key === 'Enter' &&
                    !isLoadingProperties &&
                    handleSearchByName()
                  }
                  disabled={isLoadingProperties}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)', // Dimmed text when disabled
                    },
                  }}
                />
                {searchTerm && !isSearchingByName && (
                  <IconButton
                    onClick={() => {
                      if (!isLoadingProperties) {
                        setSearchTerm('');
                        setSearchResults([]);
                      }
                    }}
                    size='small'
                    disabled={isLoadingProperties}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                )}
                {isSearchingByName ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    onClick={handleSearchByName}
                    size='small'
                    disabled={isLoadingProperties}
                  >
                    <SearchIcon fontSize='small' />
                  </IconButton>
                )}
              </Box>
            )}

            {showNameSearch &&
              searchResults.length > 0 &&
              !isLoadingProperties && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: 70,
                    left: 80,
                    zIndex: 1000,
                    width: '250px',
                    maxHeight: '300px',
                    overflow: 'auto',
                    boxShadow: 3,
                    borderRadius: '4px',
                  }}
                >
                  <List dense>
                    {searchResults
                      .filter(
                        (result, index, self) =>
                          index === self.findIndex((r) => r.id === result.id),
                      )
                      .map((result, index) => (
                        <ListItem
                          key={`${result.id}-${index}`}
                          button
                          onClick={() => {
                            if (result.geometry?.coordinates) {
                              const coordinates =
                                turf.centerOfMass(result).geometry.coordinates;
                              mapRef.current.flyTo({
                                center: coordinates,
                                zoom: 14,
                                essential: true,
                              });
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              result.properties?.listingName ||
                              result.properties?.name ||
                              'Unnamed property'
                            }
                            secondary={result.properties?.primaryUseType || ''}
                          />
                        </ListItem>
                      ))}
                  </List>
                </Paper>
              )}

            {/* Bottom buttons */}
            <Box
              sx={{
                position: 'absolute',
                bottom: isMobile() ? 100 : 24,
                left: 0,
                right: 0,
                zIndex: 1000,
              }}
            >
              <Box
                sx={{
                  float: 'left',
                  ml: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                {showSecondaryIcons && (
                  <>
                    <Tooltip title='Listing Reports'>
                      <Badge
                        badgeContent={
                          isLoadingCompReports ? 0 : compReports.length
                        }
                        color='primary'
                      >
                        <IconButton
                          onClick={() => setShowCompReports(!showCompReports)}
                          disabled={isLoadingCompReports}
                          sx={{
                            backgroundColor: 'white',
                            boxShadow: 3,
                            '&:hover': {
                              backgroundColor: '#f0f0f0',
                              transform: isLoadingCompReports
                                ? 'none'
                                : 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {isLoadingCompReports ? (
                            <CircularProgress
                              size={24}
                              color='primary'
                              thickness={5}
                            />
                          ) : (
                            <InsertDriveFileIcon />
                          )}
                        </IconButton>
                      </Badge>
                    </Tooltip>
                  </>
                )}
              </Box>

              <Box sx={{ float: 'right', mr: 3 }}>
                <Tooltip title='View Selected Sites'>
                  <IconButton
                    onClick={() => setCartOpen(true)}
                    sx={{
                      backgroundColor: 'white',
                      boxShadow: 3,
                      '&:hover': { backgroundColor: '#f0f0f0' },
                    }}
                  >
                    <Badge badgeContent={cartItems.length} color='primary'>
                      <ShoppingCartIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Sidebars */}
      <PropertyFilterSidebar
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        selectedUseTypes={selectedUseTypes}
        setSelectedUseTypes={setSelectedUseTypes}
        minAcreage={minAcreage}
        setMinAcreage={setMinAcreage}
        maxAcreage={maxAcreage}
        setMaxAcreage={setMaxAcreage}
        handleApplyFilters={handleFetchRecords}
      />

      <PropertyCartSidebar
        cartOpen={cartOpen}
        setCartOpen={setCartOpen}
        cartItems={cartItems}
        togglePolygonSelection={togglePolygonSelection}
        reportGeoData={reportGeoData}
        setShowReport={() => {
          // Capture current map state
          setReportBuilderState((prev) => ({
            ...prev,
            mapState: {
              center: mapRef.current.getCenter(),
              zoom: mapRef.current.getZoom(),
              bounds: mapRef.current.getBounds(),
            },
            geoData: reportGeoData,
          }));

          setNavigationState((prev) => ({
            ...prev,
            showReport: true,
            showMap: false,
          }));
        }}
        setCartItems={setCartItems}
        setSelectedPolygons={setSelectedPolygons}
        onClearAll={handleClearAll}
        setReportData={setReportData}
      />

      <CompReportsSidebar
        reports={compReports}
        isExpanded={showCompReports}
        toggleExpanded={() => setShowCompReports(!showCompReports)}
        isLoading={isLoadingCompReports}
        onOpenPDF={(report) => {
          window.open(report.pdf_url, '_blank');
        }}
        onLoadSession={(report) => {
          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [report.report_center_lng, report.report_center_lat],
              zoom: 14,
              essential: true,
            });
          }
          //console.log('Comps in this report:', report.comp_ids);
        }}
        onEditReport={(report) => {
          // Capture current map state
          setReportBuilderState((prev) => ({
            ...prev,
            mapState: {
              center: mapRef.current.getCenter(),
              zoom: mapRef.current.getZoom(),
              bounds: mapRef.current.getBounds(),
            },
          }));

          setNavigationState({
            showMap: false,
            showDashboard: false,
            showReport: true,
            showPreview: false,
            editingReport: report,
          });
        }}
        onViewAllReports={() => {
          // Navigate to dashboard
          setNavigationState({
            showMap: false,
            showDashboard: true,
            showReport: false,
            showPreview: false,
            editingReport: null,
          });
        }}
      />

      {/* Report Builder */}
      {console.log(
        'DEBUG - reportGeoData before ReportBuilder:',
        reportGeoData,
      )}
      {navigationState.showReport && (
        <ReportBuilder
          cartItems={reportBuilderState?.cartItems || cartItems}
          initialGeoData={reportBuilderState?.geoData || reportGeoData}
          setCartItems={setCartItems}
          savedReport={
            reportBuilderState ? null : navigationState.editingReport
          }
          onBack={() => handleReturnToMap()} // No data = simple return
          onReturnToMap={handleReturnToMap} // With data = full UX
          onNavigateToMap={() => handleReturnToMap()} // Simple return
          onRemoveItem={(id) => {
            setSelectedPolygons((prev) => {
              const updated = new Set(prev);
              updated.delete(id);
              return updated;
            });

            if (mapRef.current) {
              mapRef.current.setFeatureState(
                { source: 'property-polygons', id },
                { selected: false },
              );
            }

            setCartItems((prev) => prev.filter((item) => item.id !== id));
          }}
          mapViewState={mapViewState}
          onPreviewPDF={handleShowPreview}
          userData={userData}
          isEditingReport={!!navigationState.editingReport}
          initialRows={reportBuilderState?.rows}
          initialRowOrder={reportBuilderState?.rowOrder}
          initialColumnSettings={reportBuilderState?.columnSettings}
          initialReportTitle={reportBuilderState?.reportTitle}
          reportId={reportEditingContext.id}
          reportStatus={reportEditingContext.status}
          onContextUpdate={(newContext) => setReportEditingContext(newContext)}
          rows={liveRows || []} // ✅ default to []
          setRows={setLiveRows || (() => {})} // ✅ fallback to no-op
          reportData={reportData}
          initialOfferSortModel={reportBuilderState?.offerSortModel}
          initialFeedbackSortModel={reportBuilderState?.feedbackSortModel}
          initialStatusFilter={reportBuilderState?.statusFilter}
          initialDateFilter={reportBuilderState?.dateFilter}
          initialActiveTab={reportBuilderState?.activeTab}
        />
      )}

      {/* Dashboard */}
      {navigationState.showDashboard && (
        <CompDashboard
          userData={userData}
          onClose={handleReturnToMap}
          onEditReport={handleEditReport}
          navigationState={navigationState}
        />
      )}

      {/* Preview */}
      {navigationState.showPreview && (
        <CompPreview
          reportId={reportEditingContext.id}
          reportStatus={reportEditingContext.status}
          onContextUpdate={(newContext) => setReportEditingContext(newContext)}
          pdfBlob={pdfBlob}
          pdfUrl={pdfUrl}
          reportData={{
            ...reportData,
            currentRows: reportBuilderState?.currentRows,
            currentCartItems: reportBuilderState?.cartItems,
            currentRowOrder: reportBuilderState?.rowOrder,
            columnSettings: {
              offers: reportBuilderState?.columnSettings?.offers || {},
              feedback: reportBuilderState?.columnSettings?.feedback || {},
            },
            dataGridState: reportBuilderState?.dataGridState,
            excludeOffers: reportBuilderState?.excludeOffers,
            excludeFeedback: reportBuilderState?.excludeFeedback,
            offers: reportBuilderState?.offers || reportData?.offers,
            feedback: reportBuilderState?.feedback || reportData?.feedback,
            offerRowOrder: reportBuilderState?.offerRowOrder,
            feedbackRowOrder: reportBuilderState?.feedbackRowOrder,
            offerSortModel: reportBuilderState?.offerSortModel,
            feedbackSortModel: reportBuilderState?.feedbackSortModel,
            statusFilter: reportBuilderState?.statusFilter,
            dateFilter: reportBuilderState?.dateFilter,
            activeTab: reportBuilderState?.activeTab,
          }}
          geoData={reportBuilderState?.geoData || reportData?.geoData}
          recordCount={reportData?.rows?.length || 0}
          onBack={(returnData) => {
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
            setReportBuilderState({
              ...reportBuilderState,
              ...returnData,
              cartItems: returnData.currentCartItems,
              rows: returnData.currentRows,
              rowOrder: returnData.currentRowOrder,
              columnSettings: returnData.column_settings ||
                returnData.columnSettings || {
                  offers: {},
                  feedback: {},
                },
              geoData: returnData.reportGeoData,
              excludeOffers: returnData.excludeOffers,
              excludeFeedback: returnData.excludeFeedback,
              offers: returnData.offers,
              feedback: returnData.feedback,
              offerRowOrder: returnData.offerRowOrder,
              feedbackRowOrder: returnData.feedbackRowOrder,
              offerSortModel: returnData.offerSortModel,
              feedbackSortModel: returnData.feedbackSortModel,
              statusFilter: returnData.statusFilter,
              dateFilter: returnData.dateFilter,
              activeTab: returnData.activeTab,
            });
            setNavigationState({
              ...navigationState,
              showPreview: false,
              showReport: true,
            });
          }}
        />
      )}
    </Box>
  );
};

export default MapPage;
